import { SETTINGS_STORAGE_KEY } from "../config/constants.ts";
import type { AppSettings } from "../utils/types.ts";
import { DEFAULT_SETTINGS, validateSettings } from "./schema.ts";

export class SettingsStore {
  private value: AppSettings;
  private listeners = new Set<(settings: AppSettings) => void>();

  constructor() {
    this.value = this.read();
  }

  get(): AppSettings {
    return this.value;
  }

  set(patch: Partial<AppSettings>): AppSettings {
    this.value = validateSettings({ ...this.value, ...patch });
    this.persist();
    for (const listener of this.listeners) listener(this.value);
    return this.value;
  }

  subscribe(listener: (settings: AppSettings) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private read(): AppSettings {
    try {
      const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
      if (!raw) return { ...DEFAULT_SETTINGS };
      return validateSettings(JSON.parse(raw) as unknown);
    } catch {
      return { ...DEFAULT_SETTINGS };
    }
  }

  private persist(): void {
    const safe = {
      privacyMode: this.value.privacyMode,
      concealmentType: this.value.concealmentType,
      concealmentStrength: this.value.concealmentStrength,
      cameraPanelSize: this.value.cameraPanelSize,
      cameraPanelPosition: this.value.cameraPanelPosition,
      particleQuality: this.value.particleQuality,
      colourTheme: this.value.colourTheme,
      gestureSensitivity: this.value.gestureSensitivity,
      gestureSmoothing: this.value.gestureSmoothing,
      soundEnabled: this.value.soundEnabled,
      volume: this.value.volume,
      showFps: this.value.showFps,
      debugMode: false,
      presentationMode: this.value.presentationMode,
      mirrorCamera: this.value.mirrorCamera,
    } satisfies AppSettings;
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(safe));
    } catch {
      /* ignore quota / private-mode failures */
    }
  }
}
